import { db } from "../firebaseClient"
import { doc, setDoc, updateDoc, deleteDoc, collection, getDocs, serverTimestamp } from "firebase/firestore"
import { listOrgLearners } from "./learnerHelpers"
import { listPrograms } from "./programHelpers"
import { listOrgOrders } from "./orderHelpers"
import { listCohorts } from "./cohortHelpers"

// Filter types the Segment builder supports. Skill and Location from the
// original spec are intentionally omitted — nothing in the codebase tracks
// either against a learner (no skills taxonomy anywhere, no location field
// on users/{uid} or enrollments), so exposing them would just be dead UI.
export const FILTER_TYPES = [
  {
    key: "progress",
    label: "Progress",
    valueType: "number",
    operators: [
      { value: "gte", label: "At least" },
      { value: "lte", label: "At most" },
    ],
  },
  {
    key: "course",
    label: "Course",
    valueType: "course",
    operators: [
      { value: "enrolled", label: "Enrolled in" },
      { value: "not_enrolled", label: "Not enrolled in" },
    ],
  },
  {
    key: "program",
    label: "Program",
    valueType: "program",
    operators: [{ value: "enrolled", label: "Enrolled in" }],
  },
  {
    key: "cohort",
    label: "Cohort",
    valueType: "cohort",
    operators: [{ value: "in", label: "In cohort" }],
  },
  {
    key: "assessment",
    label: "Assessment",
    valueType: "none",
    operators: [
      { value: "passed", label: "Passed at least one" },
      { value: "failed", label: "Failed at least one" },
      { value: "none", label: "Hasn't attempted any" },
    ],
  },
  {
    key: "credential",
    label: "Credential",
    valueType: "none",
    operators: [
      { value: "has", label: "Has earned a credential" },
      { value: "none", label: "Has not earned a credential" },
    ],
  },
  {
    key: "purchase",
    label: "Purchase",
    valueType: "none",
    operators: [
      { value: "paid", label: "Has a paid order" },
      { value: "none", label: "No paid order" },
    ],
  },
  {
    key: "activity",
    label: "Activity",
    valueType: "activityStatus",
    operators: [{ value: "is", label: "Status is" }],
  },
  {
    key: "date",
    label: "Enrollment Date",
    valueType: "date",
    operators: [
      { value: "after", label: "Enrolled after" },
      { value: "before", label: "Enrolled before" },
    ],
  },
]

export const ACTIVITY_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "at-risk", label: "At Risk" },
  { value: "inactive", label: "Inactive" },
]

// Everything a filter needs to evaluate against, fetched once and reused
// across every filter row in the builder — avoids re-querying Firestore per
// filter row or per keystroke while the admin is building a segment.
export const buildAudience = async (orgId) => {
  const [{ learners }, { programs }, { orders }, { cohorts }] = await Promise.all([
    listOrgLearners(orgId),
    listPrograms(orgId),
    listOrgOrders(orgId),
    listCohorts(orgId),
  ])
  return { learners: learners || [], programs: programs || [], orders: orders || [], cohorts: cohorts || [] }
}

const evaluateOne = (learner, filter, audience) => {
  const { type, operator, value } = filter
  switch (type) {
    case "progress": {
      const v = Number(value)
      if (Number.isNaN(v)) return true
      return operator === "gte" ? (learner.avgProgress || 0) >= v : (learner.avgProgress || 0) <= v
    }
    case "course": {
      const isEnrolled = learner.enrollments?.some((e) => e?.courseId === value)
      return operator === "enrolled" ? !!isEnrolled : !isEnrolled
    }
    case "program": {
      const program = audience.programs?.find((p) => p?.programId === value)
      const courseIds = program?.courseIds || []
      return !!learner.enrollments?.some((e) => courseIds.includes(e?.courseId))
    }
    case "cohort": {
      return !!learner.enrollments?.some((e) => e?.cohortId === value)
    }
    case "assessment": {
      const attempts = []
      learner.enrollments?.forEach((e) => {
        Object.values(e?.moduleQuizResults || {})?.forEach((r) => {
          if (r?.attemptCount) attempts.push(!!r?.passed)
        })
        if (e?.capstoneResult?.attemptCount) attempts.push(!!e?.capstoneResult?.passed)
      })
      if (operator === "passed") return attempts.some((p) => p === true)
      if (operator === "failed") return attempts.some((p) => p === false)
      return attempts.length === 0
    }
    case "credential": {
      const hasCredential = (learner.credentialsCount || 0) > 0
      return operator === "has" ? hasCredential : !hasCredential
    }
    case "purchase": {
      const hasPaid = audience.orders?.some((o) => o?.customerId === learner.userId && o?.status === "paid")
      return operator === "paid" ? hasPaid : !hasPaid
    }
    case "activity": {
      return learner.status === value
    }
    case "date": {
      if (!value || !learner.enrolledAtMs) return false
      const cutoff = new Date(value).getTime()
      return operator === "after" ? learner.enrolledAtMs >= cutoff : learner.enrolledAtMs <= cutoff
    }
    default:
      return true
  }
}

// AND across every filter row — matches the standard "narrow the audience"
// semantics of segment/audience builders. An empty filter list matches everyone.
export const applyFilters = (learners, filters, audience) => {
  if (!filters?.length) return learners || []
  return learners?.filter((learner) => filters.every((f) => evaluateOne(learner, f, audience))) || []
}

export const createSegment = async ({ orgId, name, description, filters }) => {
  if (!orgId || !name?.trim()) {
    return { segmentId: null, error: { message: "Segment name is required" } }
  }
  try {
    const ref = doc(collection(db, "organizations", orgId, "segments"))
    await setDoc(ref, {
      name: name.trim(),
      description: description || "",
      filters: filters || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return { segmentId: ref.id, error: null }
  } catch (error) {
    return { segmentId: null, error }
  }
}

export const updateSegment = async (orgId, segmentId, updates) => {
  try {
    await updateDoc(doc(db, "organizations", orgId, "segments", segmentId), {
      ...updates,
      updatedAt: serverTimestamp(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const deleteSegment = async (orgId, segmentId) => {
  try {
    await deleteDoc(doc(db, "organizations", orgId, "segments", segmentId))
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const listSegments = async (orgId) => {
  try {
    const snap = await getDocs(collection(db, "organizations", orgId, "segments"))
    return { segments: snap.docs?.map((d) => ({ segmentId: d.id, ...d.data() })), error: null }
  } catch (error) {
    return { segments: [], error }
  }
}